import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { Plus, ArrowLeft, Pencil, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/atoms/sheet';
import {
  DataTableDrawerHeader,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
} from '@/components/molecules';
import { DataTableFilterSection } from '@/components/organisms';
import type { DataGridSortDir } from '@/components/organisms/data-grid';
import { ChecklistTemplateList } from '@/components/organisms/checklists/ChecklistTemplateList';
import { ChecklistTemplateReadOnlyBody } from '@/components/organisms/checklists/ChecklistTemplateReadOnlyBlocks';
import { ChecklistOutlineBuilderPage } from '@/components/organisms/checklists/ChecklistOutlineBuilderPage';
import { DetailPageLayout, FormPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import {
  useChecklistTemplates,
  useChecklistTemplate,
  useCreateChecklistTemplate,
  useUpdateChecklistTemplate,
  useArchiveChecklistTemplate,
  useRestoreChecklistTemplate,
  useDuplicateChecklistTemplate,
  useChecklistTemplateVersions,
} from '@/hooks/settings/useChecklists';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import {
  buildUpdateDtoFromFormValues,
  dtoFromFormValues,
  linksFromTemplate,
  type SelectedJobTypeLink,
} from '@/lib/checklists/templateForm';
import {
  CHECKLIST_PAGE_SIZE,
  CHECKLIST_STATUS_OPTIONS,
  compareChecklistValues,
  getChecklistSortValue,
  type ChecklistColumn,
  type ChecklistPageStatusFilter,
} from '@/lib/checklists/templateList';
import type { ChecklistTemplate } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/checklist')({
  component: ChecklistLayout,
});

function ChecklistLayout() {
  return <ChecklistPage />;
}

// ─── Page ───────────────────────────────────────────────────────────────────

const DETAIL_ROUTE_PATTERN = /^\/dashboard\/settings\/checklist\/([^/]+)\/?$/;
const EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/checklist\/([^/]+)\/edit\/?$/;
const NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/checklist\/new\/?$/;

export function ChecklistPage() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: templatesData, isLoading } = useChecklistTemplates();
  const { data: jobTypes = [] } = useJobTypes();
  const archive = useArchiveChecklistTemplate();
  const restore = useRestoreChecklistTemplate();
  const duplicate = useDuplicateChecklistTemplate();

  const createOpen = NEW_ROUTE_PATTERN.test(pathname);
  const detailMatch = DETAIL_ROUTE_PATTERN.exec(pathname);
  const editMatch = EDIT_ROUTE_PATTERN.exec(pathname);
  const detailChecklistId = detailMatch?.[1] ? decodeURIComponent(detailMatch[1]) : undefined;
  const editChecklistId = editMatch?.[1] ? decodeURIComponent(editMatch[1]) : undefined;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ChecklistPageStatusFilter>('active');
  const [sortKey, setSortKey] = useState<ChecklistColumn>('definition');
  const [sortDir, setSortDir] = useState<DataGridSortDir>('asc');
  const [page, setPage] = useState(1);

  const templates = useMemo(() => templatesData?.data ?? [], [templatesData?.data]);
  const selectedTemplate = useMemo(
    () => (detailChecklistId ? (templates.find((t) => t.id === detailChecklistId) ?? null) : null),
    [detailChecklistId, templates],
  );
  const editTemplate = useMemo(
    () => (editChecklistId ? (templates.find((t) => t.id === editChecklistId) ?? null) : null),
    [editChecklistId, templates],
  );

  const jobTypeNames = useCallback(
    (template: ChecklistTemplate): string => {
      const links = linksFromTemplate(template);
      return links
        .map(
          (link) => jobTypes.find((jt) => jt.id === link.jobTypeId)?.name ?? 'Job type unavailable',
        )
        .join(', ');
    },
    [jobTypes],
  );

  const totalItems = useCallback((tpl: ChecklistTemplate): number => {
    return tpl.sections.reduce((sum, s) => sum + s.items.length, 0);
  }, []);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const isArchived = template.status === 'ARCHIVED' || template.isDeleted;
      const matchesStatus = statusFilter === 'archived' ? isArchived : !isArchived;
      if (!matchesStatus) return false;

      const query = search.trim().toLowerCase();
      if (!query) return true;

      return [
        template.name,
        template.description ?? '',
        template.defaultVisibility ?? 'INTERNAL',
        template.status ?? 'ACTIVE',
        jobTypeNames(template),
        ...template.sections.map((section) => section.name),
        ...template.sections.flatMap((section) => section.items.map((item) => item.label)),
      ]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [jobTypeNames, search, statusFilter, templates]);

  const openCreate = useCallback(() => {
    void navigate({ to: '/dashboard/settings/checklist/new' });
  }, [navigate]);

  const openEdit = useCallback(
    (tpl: ChecklistTemplate) => {
      void navigate({
        to: '/dashboard/settings/checklist/$id/edit',
        params: { id: tpl.id },
      });
    },
    [navigate],
  );

  const openView = useCallback(
    (tpl: ChecklistTemplate) => {
      void navigate({
        to: '/dashboard/settings/checklist/$id',
        params: { id: tpl.id },
      });
    },
    [navigate],
  );

  const sortedTemplates = useMemo(() => {
    return [...filteredTemplates].sort((a, b) => {
      const result = compareChecklistValues(
        getChecklistSortValue(sortKey, a, jobTypeNames, totalItems),
        getChecklistSortValue(sortKey, b, jobTypeNames, totalItems),
      );
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredTemplates, jobTypeNames, sortDir, sortKey, totalItems]);

  const activeFilterCount = statusFilter !== 'active' ? 1 : 0;
  const totalPages = Math.max(1, Math.ceil(sortedTemplates.length / CHECKLIST_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageTemplates = sortedTemplates.slice(
    (currentPage - 1) * CHECKLIST_PAGE_SIZE,
    currentPage * CHECKLIST_PAGE_SIZE,
  );

  function resetFilters() {
    setSearch('');
    setStatusFilter('active');
    setPage(1);
  }

  function handleSortChange(key: ChecklistColumn | null, dir: DataGridSortDir | null) {
    if (!key || !dir) return;
    setSortKey(key);
    setSortDir(dir);
    setPage(1);
  }

  function closeDrawer() {
    void navigate({ to: '/dashboard/settings/checklist' });
  }

  const filterContent = (
    <>
      <DataTableFilterSection
        label="Checklist"
        active={!!search}
        onClear={() => {
          setSearch('');
          setPage(1);
        }}
      >
        <DataTableSearchFilterField
          value={search}
          onValueChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search by name, job type, section, or item..."
        />
      </DataTableFilterSection>
      <DataTableFilterSection
        label="Status"
        active={statusFilter !== 'active'}
        onClear={() => {
          setStatusFilter('active');
          setPage(1);
        }}
      >
        <DataTableSelectFilterField
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value as ChecklistPageStatusFilter);
            setPage(1);
          }}
          placeholder="Active"
          options={CHECKLIST_STATUS_OPTIONS}
        />
      </DataTableFilterSection>
    </>
  );

  // New / Edit render as a full-screen two-pane builder page (not the Sheet).
  if (createOpen || editChecklistId) {
    return (
      <>
        <Fullbleed />
        <ChecklistDefinitionFormRouteContent
          definitionId={editChecklistId}
          mode={createOpen ? 'create' : 'edit'}
          prefetchedTemplate={editTemplate}
          onClose={closeDrawer}
          onBack={
            editChecklistId
              ? () =>
                  void navigate({
                    to: '/dashboard/settings/checklist/$id',
                    params: { id: editChecklistId },
                  })
              : undefined
          }
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Fullbleed />
        <DetailPageLayout
          header={
            <>
              <div className="min-w-0">
                <h1 className="truncate text-base font-semibold">Checklists</h1>
                <p className="text-xs text-muted-foreground">
                  Build reusable checklist definitions for appointment workflows.
                </p>
              </div>
            </>
          }
          contentClassName="flex min-h-0 flex-col p-4"
        >
          <Skeleton className="h-full min-h-80 w-full" />
        </DetailPageLayout>
      </>
    );
  }

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Checklists</h1>
              <p className="text-xs text-muted-foreground">
                Build reusable checklist definitions and link them to job types.
              </p>
            </div>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add checklist
            </Button>
          </>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        <ChecklistTemplateList
          templates={pageTemplates}
          sourceCount={templates.length}
          totalCount={sortedTemplates.length}
          isLoading={isLoading}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          showClearAll={Boolean(search) || activeFilterCount > 0}
          activeFilterCount={activeFilterCount}
          filterContent={filterContent}
          onResetFilters={resetFilters}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          onView={(template) => openView(template)}
          onEdit={(template) => openEdit(template)}
          onDuplicate={(template) =>
            duplicate.mutate(template.id, {
              onSuccess: (created) => openEdit(created),
            })
          }
          onArchive={(template) => archive.mutate(template.id)}
          onRestore={(template) => restore.mutate(template.id)}
          archivePending={archive.isPending}
          restorePending={restore.isPending}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          jobTypeNames={jobTypeNames}
          totalItems={totalItems}
        />
      </DetailPageLayout>

      <Sheet open={Boolean(detailChecklistId)}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-5xl"
          showCloseButton={false}
          onPointerDownOutside={(e) => {
            e.preventDefault();
            closeDrawer();
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            closeDrawer();
          }}
        >
          {detailChecklistId ? (
            <ChecklistDetailSheetContent
              template={selectedTemplate}
              definitionId={detailChecklistId}
              jobTypes={jobTypes}
              onClose={closeDrawer}
              onEdit={() =>
                void navigate({
                  to: '/dashboard/settings/checklist/$id/edit',
                  params: { id: detailChecklistId },
                })
              }
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─── Routeable detail/form shells ───────────────────────────────────────────

interface ChecklistDefinitionFormRouteContentProps {
  definitionId?: string;
  mode?: 'create' | 'edit';
  prefetchedTemplate?: ChecklistTemplate | null;
  onClose?: () => void;
}

interface ChecklistDefinitionDetailRouteContentProps {
  definitionId: string;
}

export function ChecklistDefinitionFormRouteContent({
  definitionId,
  mode,
  prefetchedTemplate,
  onClose,
  onBack,
}: ChecklistDefinitionFormRouteContentProps & { onBack?: () => void }) {
  const navigate = useNavigate();
  const isEdit = mode === 'edit' || Boolean(definitionId);
  const templateQuery = useChecklistTemplate(definitionId ?? null);
  const createTemplate = useCreateChecklistTemplate();
  const updateTemplate = useUpdateChecklistTemplate();
  const [selectedJobTypeLinks, setSelectedJobTypeLinks] = useState<SelectedJobTypeLink[]>([]);

  const template = prefetchedTemplate ?? templateQuery.data ?? null;

  useEffect(() => {
    setSelectedJobTypeLinks(template ? linksFromTemplate(template) : []);
  }, [template]);

  const close = onClose ?? (() => void navigate({ to: '/dashboard/settings/checklist' }));

  if (isEdit && templateQuery.isLoading) {
    return (
      <FormPageLayout title="Edit checklist" onBack={onBack ?? close}>
        <div className="mx-auto max-w-[1180px] space-y-4 px-4 py-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </FormPageLayout>
    );
  }

  if (isEdit && !template) {
    return (
      <FormPageLayout title="Edit checklist" onBack={onBack ?? close}>
        <div className="px-5 py-5 text-sm text-muted-foreground">
          Checklist definition not found.
        </div>
      </FormPageLayout>
    );
  }

  return (
    <ChecklistOutlineBuilderPage
      onClose={close}
      onBack={onBack}
      editing={template}
      selectedJobTypeLinks={selectedJobTypeLinks}
      onChangeJobTypeLinks={setSelectedJobTypeLinks}
      onSubmit={(values) => {
        if (template) {
          const dto = buildUpdateDtoFromFormValues(values, selectedJobTypeLinks, template);
          updateTemplate.mutate(
            { id: template.id, ...dto },
            {
              onSuccess: (updated) => {
                void navigate({
                  to: '/dashboard/settings/checklist/$id',
                  params: { id: updated.id },
                });
              },
            },
          );
          return;
        }

        const dto = dtoFromFormValues(values, selectedJobTypeLinks);
        createTemplate.mutate(dto, {
          onSuccess: (created) => {
            void navigate({
              to: '/dashboard/settings/checklist/$id',
              params: { id: created.id },
            });
          },
        });
      }}
      isPending={createTemplate.isPending || updateTemplate.isPending}
    />
  );
}

export function ChecklistDefinitionDetailRouteContent({
  definitionId,
}: ChecklistDefinitionDetailRouteContentProps) {
  const navigate = useNavigate();
  const templateQuery = useChecklistTemplate(definitionId);
  const { data: jobTypes = [] } = useJobTypes();
  const template = templateQuery.data ?? null;

  if (templateQuery.isLoading) {
    return (
      <div className="max-w-4xl space-y-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="max-w-4xl p-4 text-sm text-muted-foreground">
        Checklist definition not found.
      </div>
    );
  }

  return (
    <ChecklistDefinitionDetail
      template={template}
      jobTypes={jobTypes}
      onClose={() => void navigate({ to: '/dashboard/settings/checklist' })}
      onEdit={() =>
        void navigate({
          to: '/dashboard/settings/checklist/$id/edit',
          params: { id: definitionId },
        })
      }
    />
  );
}

// ─── View dialog (read-only) ────────────────────────────────────────────────

export function ChecklistDefinitionDetail({
  template,
  jobTypes,
  onClose,
  onEdit,
}: {
  template: ChecklistTemplate | null;
  jobTypes: { id: string; name: string; color: string }[];
  onClose: () => void;
  onEdit: () => void;
}) {
  const versionsQuery = useChecklistTemplateVersions(template?.id ?? null);

  if (!template) return null;

  const versions = versionsQuery.data ?? [];

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="flex min-w-0 items-center gap-2">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Back to checklists"
                onClick={onClose}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="flex min-w-0 items-center gap-2 text-base font-semibold">
                  <span className="truncate">{template.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    v{template.version ?? 1}
                  </Badge>
                </h1>
                {template.description ? (
                  <p className="truncate text-xs text-muted-foreground">{template.description}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Checklist definition</p>
                )}
              </div>
            </div>
            <Button
              size="icon-sm"
              aria-label="Edit checklist"
              title="Edit checklist"
              onClick={onEdit}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </>
        }
        contentClassName="min-h-0 overflow-y-auto p-4 sm:p-6"
      >
        <ChecklistTemplateReadOnlyBody
          template={template}
          jobTypes={jobTypes}
          versions={versions}
          versionsLoading={versionsQuery.isLoading}
        />
      </DetailPageLayout>
    </>
  );
}

// ─── Detail Sheet (row click → Sheet) ───────────────────────────────────────

function ChecklistDetailSheetContent({
  template: prefetchedTemplate,
  definitionId,
  jobTypes,
  onClose,
  onEdit,
}: {
  template: ChecklistTemplate | null;
  definitionId: string;
  jobTypes: { id: string; name: string; color: string }[];
  onClose: () => void;
  onEdit: () => void;
}) {
  const templateQuery = useChecklistTemplate(prefetchedTemplate ? null : definitionId);
  const template = prefetchedTemplate ?? templateQuery.data ?? null;
  const versionsQuery = useChecklistTemplateVersions(template?.id ?? null);

  if (templateQuery.isLoading && !template) {
    return (
      <>
        <DataTableDrawerHeader title="Checklist" onClose={onClose} />
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    );
  }

  if (!template) {
    return (
      <>
        <DataTableDrawerHeader title="Checklist" onClose={onClose} />
        <p className="p-5 text-sm text-muted-foreground">Checklist definition not found.</p>
      </>
    );
  }

  const versions = versionsQuery.data ?? [];

  return (
    <>
      <SheetHeader className="border-b px-5 py-4">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <SheetTitle className="flex min-w-0 items-center gap-2 text-base font-semibold">
              <span className="truncate">{template.name}</span>
              <Badge variant="outline" className="shrink-0 text-[10px]">
                v{template.version ?? 1}
              </Badge>
            </SheetTitle>
            {template.description ? (
              <p className="truncate text-xs text-muted-foreground">{template.description}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Checklist definition</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Edit"
              onClick={onEdit}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <ChecklistTemplateReadOnlyBody
          template={template}
          jobTypes={jobTypes}
          versions={versions}
          versionsLoading={versionsQuery.isLoading}
        />
      </div>
    </>
  );
}
